import type { CostpointType, DraftCostpointAssignment, DraftTransaction, Tag } from '@finapp/types';
import type React from 'react';
import { useEffect, useState } from 'react';
import { apiUrl } from '../config/api';
import { SingleTagSelector } from './SingleTagSelector';

interface CostpointAssignmentEditorProps {
  transaction: DraftTransaction;
  onUpdate: (costpoints: DraftCostpointAssignment[]) => void;
}

interface Costpoint {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export const CostpointAssignmentEditor: React.FC<CostpointAssignmentEditorProps> = ({
  transaction,
  onUpdate,
}) => {
  const [costpoints, setCostpoints] = useState<Costpoint[]>([]);
  const [newCostpoint, setNewCostpoint] = useState<CostpointType>('cs' as CostpointType);
  const [newPercentage, setNewPercentage] = useState<number>(100);
  const [newAmount, setNewAmount] = useState<number>(0);
  const [splitMode, setSplitMode] = useState<'percentage' | 'amount'>('percentage');

  // Fetch costpoints from API
  useEffect(() => {
    const fetchCostpoints = async () => {
      try {
        const response = await fetch(apiUrl('costpoints'), {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setCostpoints(data);
          if (data.length > 0) {
            setNewCostpoint(data[0].name);
          }
        }
      } catch (error) {
        console.error('Failed to fetch costpoints:', error);
        // Fallback to default costpoints
        setCostpoints([
          { id: 1, name: 'cs', created_at: '', updated_at: '' },
          { id: 2, name: 'pubg', created_at: '', updated_at: '' },
          { id: 3, name: 'admin', created_at: '', updated_at: '' },
          { id: 4, name: 'other', created_at: '', updated_at: '' },
        ]);
      }
    };

    fetchCostpoints();
  }, []);

  const transactionAmount = Math.abs(transaction.amount);
  const totalAssignedPercentage = transaction.costpoints.reduce(
    (sum, cp) => sum + cp.percentage,
    0
  );
  const totalAssignedAmount = transaction.costpoints.reduce((sum, cp) => sum + cp.amount, 0);
  const remainingPercentage = 100 - totalAssignedPercentage;
  const remainingAmount = transactionAmount - totalAssignedAmount;

  const handleAddCostpoint = () => {
    if (splitMode === 'percentage') {
      if (remainingPercentage <= 0) return;

      const percentage = Math.min(newPercentage, remainingPercentage);
      const amount = (transactionAmount * percentage) / 100;

      const newAssignment: DraftCostpointAssignment = {
        costpoint: newCostpoint,
        amount: Math.round(amount * 100) / 100, // Round to 2 decimal places
        percentage: Math.round(percentage * 100) / 100, // Round to 2 decimal places
        tags: [], // Start with no tags
      };

      onUpdate([...transaction.costpoints, newAssignment]);

      // Auto-suggest remaining percentage for next split
      const newRemainingPercentage = remainingPercentage - percentage;
      setNewPercentage(Math.max(0, Math.min(100, newRemainingPercentage)));
    } else {
      if (remainingAmount <= 0) return;

      const amount = Math.min(newAmount, remainingAmount);
      const percentage = (amount / transactionAmount) * 100;

      const newAssignment: DraftCostpointAssignment = {
        costpoint: newCostpoint,
        amount: Math.round(amount * 100) / 100, // Round to 2 decimal places
        percentage: Math.round(percentage * 100) / 100, // Round to 2 decimal places
        tags: [], // Start with no tags
      };

      onUpdate([...transaction.costpoints, newAssignment]);

      // Auto-suggest remaining amount for next split
      const newRemainingAmount = remainingAmount - amount;
      setNewAmount(Math.max(0, Math.min(transactionAmount, newRemainingAmount)));
    }
  };

  const handleRemoveCostpoint = (index: number) => {
    const updated = transaction.costpoints.filter((_, i) => i !== index);
    onUpdate(updated);
  };

  const handleUpdateCostpoint = (
    index: number,
    field: 'percentage' | 'amount' | 'tag',
    value: number | Tag | null
  ) => {
    const updated = [...transaction.costpoints];
    const assignment = { ...updated[index] };

    if (field === 'percentage' && typeof value === 'number') {
      assignment.percentage = Math.round(value * 100) / 100;
      assignment.amount =
        Math.round(((transactionAmount * assignment.percentage) / 100) * 100) / 100;
    } else if (field === 'amount' && typeof value === 'number') {
      assignment.amount = Math.round(value * 100) / 100;
      assignment.percentage = Math.round((assignment.amount / transactionAmount) * 100 * 100) / 100;
    } else if (field === 'tag' && (value === null || typeof value === 'object')) {
      // Convert single tag to array for backward compatibility
      assignment.tags = value ? [value as Tag] : [];
    }

    updated[index] = assignment as DraftCostpointAssignment;
    onUpdate(updated);
  };

  const handleQuickAssign = (costpoint: CostpointType) => {
    if (splitMode === 'percentage') {
      if (remainingPercentage <= 0) return;

      const assignment: DraftCostpointAssignment = {
        costpoint,
        amount: transactionAmount,
        percentage: 100,
        tags: [], // Start with no tags
      };

      onUpdate([assignment]);
    } else {
      if (remainingAmount <= 0) return;

      const assignment: DraftCostpointAssignment = {
        costpoint,
        amount: transactionAmount,
        percentage: 100,
        tags: [], // Start with no tags
      };

      onUpdate([assignment]);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900">Costpoint Assignment</h4>
        <div className="text-xs text-gray-500">
          Remaining: {remainingPercentage.toFixed(1)}% ({formatAmount(remainingAmount)})
        </div>
      </div>

      {/* Split Mode Toggle */}
      <div className="flex items-center space-x-4">
        <span className="text-sm font-medium text-gray-700">Split by:</span>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setSplitMode('percentage')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              splitMode === 'percentage'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Percentage
          </button>
          <button
            onClick={() => setSplitMode('amount')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              splitMode === 'amount'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Amount
          </button>
        </div>
      </div>

      {/* Current Assignments */}
      {transaction.costpoints.length > 0 && (
        <div className="space-y-2">
          {transaction.costpoints.map((assignment, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-white border rounded">
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">
                    {costpoints.find((cp) => cp.name === assignment.costpoint)?.name ||
                      assignment.costpoint}
                  </span>
                  <button
                    onClick={() => handleRemoveCostpoint(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="space-y-3 mt-2">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={assignment.percentage}
                        onChange={(e) =>
                          handleUpdateCostpoint(
                            index,
                            'percentage',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                    <div className="text-sm text-gray-500">=</div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={assignment.amount}
                        onChange={(e) =>
                          handleUpdateCostpoint(index, 'amount', parseFloat(e.target.value) || 0)
                        }
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        min="0"
                        step="0.01"
                      />
                      <span className="text-sm text-gray-500">€</span>
                    </div>
                  </div>

                  {/* Tag for this costpoint assignment (single tag only) */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Tag for this split (one tag per split)
                    </label>
                    <SingleTagSelector
                      selectedTag={assignment.tags[0] || null}
                      onTagChange={(tag) => handleUpdateCostpoint(index, 'tag', tag)}
                      placeholder="Select a tag for this split..."
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Assignment Buttons */}
      {transaction.costpoints.length === 0 && (
        <div>
          <p className="text-sm text-gray-600 mb-2">Quick assign to single costpoint:</p>
          <div className="grid grid-cols-3 gap-2">
            {costpoints.slice(0, 6).map((costpoint) => (
              <button
                key={costpoint.name}
                onClick={() => handleQuickAssign(costpoint.name as CostpointType)}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {costpoint.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Add New Costpoint */}
      {(splitMode === 'percentage' ? remainingPercentage > 0 : remainingAmount > 0) && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-sm font-medium text-blue-900 mb-3">Add Costpoint Assignment</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Costpoint</label>
              <select
                value={newCostpoint}
                onChange={(e) => setNewCostpoint(e.target.value as CostpointType)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                {costpoints.map((costpoint) => (
                  <option key={costpoint.name} value={costpoint.name}>
                    {costpoint.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {splitMode === 'percentage' ? 'Percentage' : 'Amount'}
              </label>
              {splitMode === 'percentage' ? (
                <input
                  type="number"
                  value={newPercentage}
                  onChange={(e) => setNewPercentage(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  max={remainingPercentage}
                  step="0.1"
                />
              ) : (
                <input
                  type="number"
                  value={newAmount}
                  onChange={(e) => setNewAmount(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  max={remainingAmount}
                  step="0.01"
                />
              )}
            </div>

            <div className="flex items-end">
              <button
                onClick={handleAddCostpoint}
                disabled={
                  splitMode === 'percentage'
                    ? newPercentage <= 0 || newPercentage > remainingPercentage
                    : newAmount <= 0 || newAmount > remainingAmount
                }
                className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:bg-gray-400"
              >
                Add
              </button>
            </div>
          </div>
          <p className="text-xs text-blue-700 mt-2">
            {splitMode === 'percentage' ? (
              <>Amount: {formatAmount((transactionAmount * newPercentage) / 100)}</>
            ) : (
              <>Percentage: {((newAmount / transactionAmount) * 100).toFixed(1)}%</>
            )}
          </p>
        </div>
      )}

      {/* Validation Status */}
      <div className="flex items-center justify-between text-sm">
        <div
          className={`flex items-center ${
            Math.abs(totalAssignedPercentage - 100) < 0.01 ? 'text-green-600' : 'text-yellow-600'
          }`}
        >
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {Math.abs(totalAssignedPercentage - 100) < 0.01 ? 'Complete' : 'Incomplete'}
        </div>
        <div className="text-gray-500">
          Total: {totalAssignedPercentage.toFixed(1)}% / 100% ({formatAmount(totalAssignedAmount)} /{' '}
          {formatAmount(transactionAmount)})
        </div>
      </div>
    </div>
  );
};
